/**
 * `<AgencyProvider />` + `useAgency()` — exposes two pieces of state
 * the agency-space UI reads (ADR-0237 Slice 1):
 *
 *   1. `features`    — `{ agency, white_label }`, from
 *                      `GET /v1/account/features`. Reflects the
 *                      EFFECTIVE account (home when not acting; the
 *                      client's when acting). The TopBar switcher
 *                      shows only when `features.agency` on the home
 *                      session OR when `acting` is set.
 *   2. `acting`      — the SERVER-authoritative acting state, from
 *                      `User.acting_context` on `/v1/auth/me`. `null`
 *                      when the session is not in an acting state;
 *                      otherwise the shape carries the home + acting
 *                      account names + ids + roles. No client-side
 *                      persistence — the banner shows exactly when
 *                      the server says the session is acting.
 *
 * The provider fetches `features` once per authenticated session,
 * after the AuthProvider resolves. The fetch runs only when the
 * dashboard shell is actually mounted — pre-cookie surfaces (/verify,
 * /welcome, /support-login) never trigger a /features call because
 * the AuthProvider stays in `bootstrap` / `unauthenticated` there.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';

import type { User } from '@sill/types';

import { useOptionalHttp } from '../api/http-context.js';
import { useAuth } from './auth-context.js';
import { getFeatures } from '../api/agency.js';

/**
 * Acting state surfaced on the context. Mirrors
 * `User.acting_context` (which is itself a pass-through of the server
 * `MeActingContextWire` shape) so views can read one type end-to-end.
 */
export type AgencyActing = NonNullable<User['acting_context']> | null;

export interface AgencyFeatures {
  readonly agency: boolean;
  readonly white_label: boolean;
}

/**
 * ADR-0240 Slice 2c — the acting agency's white-label chrome. `null`
 * on non-acting sessions and on acting sessions where the agency's
 * `white_label` toggle is off. Mirrors `User.acting_white_label`.
 */
export type ActingWhiteLabel = NonNullable<User['acting_white_label']> | null;

export interface AgencyCtxValue {
  /** Resolved features for the effective account. `null` until the first
   *  fetch returns; views read `features?.agency === true` safely. */
  readonly features: AgencyFeatures | null;
  readonly featuresLoading: boolean;
  readonly featuresError: string | null;
  /** Server-authoritative acting state, or `null` when not acting. */
  readonly acting: AgencyActing;
  /** ADR-0240 Slice 2c — the acting agency's white-label chrome (name
   *  + logo + accent). `null` on non-acting sessions and on acting
   *  sessions whose agency has `white_label=false`. The shell reads
   *  this to swap the top-left logo/name while acting. */
  readonly actingWhiteLabel: ActingWhiteLabel;
  /** Refetch features — views call after a mutation that changes the
   *  effective account (act-as switch, release). */
  readonly refreshFeatures: () => Promise<void>;
}

const AgencyCtx = createContext<AgencyCtxValue | null>(null);

export function useAgency(): AgencyCtxValue {
  const ctx = useContext(AgencyCtx);
  if (ctx === null) {
    throw new Error('useAgency() called outside <AgencyProvider>');
  }
  return ctx;
}

/**
 * Same shape as `useAgency` but returns `null` outside a provider —
 * for leaf components in test scaffolds that haven't wired the
 * provider tree.
 */
export function useOptionalAgency(): AgencyCtxValue | null {
  return useContext(AgencyCtx);
}

export interface AgencyProviderProps {
  readonly children: ReactNode;
  /** Test seed — fixes `features` without a network fetch. */
  readonly initialFeatures?: AgencyFeatures;
}

export function AgencyProvider({
  children,
  initialFeatures,
}: AgencyProviderProps): JSX.Element {
  const http = useOptionalHttp();
  const auth = useAuth();
  const [features, setFeatures] = useState<AgencyFeatures | null>(
    initialFeatures ?? null,
  );
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const [featuresError, setFeaturesError] = useState<string | null>(null);

  const current_user =
    auth.state.kind === 'authenticated' ? auth.state.user : null;

  // The acting state is the server-side `User.acting_context` field.
  // No localStorage; no divergence between client and server.
  const acting: AgencyActing = current_user?.acting_context ?? null;
  // ADR-0240 Slice 2c — acting-session agency chrome; server-side
  // populated only when white_label is on + a row exists.
  const actingWhiteLabel: ActingWhiteLabel =
    current_user?.acting_white_label ?? null;

  const doFetchFeatures = useCallback(async (): Promise<void> => {
    if (http === null || current_user === null) return;
    setFeaturesLoading(true);
    setFeaturesError(null);
    const result = await getFeatures(http);
    if (result.kind === 'ok') {
      setFeatures(result.features);
    } else {
      // Non-fatal: features default to `false` on error so a transient
      // service-error doesn't accidentally surface agency nav.
      setFeatures({ agency: false, white_label: false });
      setFeaturesError(result.message);
    }
    setFeaturesLoading(false);
  }, [http, current_user]);

  useEffect(() => {
    if (initialFeatures !== undefined) return;
    if (current_user === null) {
      setFeatures(null);
      return;
    }
    void doFetchFeatures();
  }, [current_user, doFetchFeatures, initialFeatures]);

  const value = useMemo<AgencyCtxValue>(
    () => ({
      features,
      featuresLoading,
      featuresError,
      acting,
      actingWhiteLabel,
      refreshFeatures: doFetchFeatures,
    }),
    [
      features,
      featuresLoading,
      featuresError,
      acting,
      actingWhiteLabel,
      doFetchFeatures,
    ],
  );

  return <AgencyCtx.Provider value={value}>{children}</AgencyCtx.Provider>;
}
